import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Stage viewport: a translate + uniform scale of the logical board space.
 *
 * The Stage receives this as `scaleX/scaleY/x/y`. We use a small custom hook
 * instead of `@use-gesture/react` because Konva's Stage already receives
 * pointer events as a regular DOM element, and a hand-rolled two-pointer
 * handler gives us perfect compatibility with single-finger drag of player
 * tokens (Konva claims the down-pointer for the shape, the second pointer is
 * what triggers our pinch logic).
 *
 * Touch model (standard canvas-app conventions):
 *   - 1 finger  → element interaction / tool gesture (owned by the editor)
 *   - 2 fingers → pinch-zoom *and* pan in one gesture, anywhere on the stage;
 *     the midpoint translation pans, the spread change zooms around it.
 *   - wheel     → zoom centred on the cursor (desktop)
 *
 * iOS specifics this hook takes care of:
 *   - `touch-action: none` on the container — without it Safari claims the
 *     second finger for the browser's own pinch (page zoom) and sends
 *     `pointercancel`, so custom pinch never receives events. This was why
 *     two-finger gestures "did nothing" on iPads.
 *   - `gesturestart/-change` preventDefault — Safari-proprietary events that
 *     trigger page zoom in browser tabs even when pointer events are handled.
 *   - move/up listeners live on the window so a gesture survives fingers
 *     drifting outside the container mid-pinch.
 */

export interface Viewport {
  scale: number;
  x: number;
  y: number;
}

export interface UsePinchZoomOptions {
  minScale?: number;
  maxScale?: number;
  /** Initial viewport — set to `fit` the pitch when the container mounts. */
  initial?: Viewport;
  /**
   * Fired once when a second touch pointer lands, i.e. a pinch/pan gesture
   * begins. Editors use it to abort whatever the FIRST finger started
   * (in-flight stroke draft, token drag, background pan) so the gesture
   * doesn't draw/move things as a side effect.
   */
  onGestureStart?: () => void;
}

export interface UsePinchZoomResult {
  viewport: Viewport;
  setViewport: (v: Viewport) => void;
  reset: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Spread on the wheel target (the Konva Stage container). */
  onWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
  /**
   * Synchronous gesture probe. Editors MUST call this at the top of their
   * Konva stage handlers and bail out while true: pointer events fire BEFORE
   * touch events for the same finger, so the second finger's `touchstart`
   * arrives AFTER our gesture began — without the guard it re-arms the
   * single-finger interaction (background pan / stage drag / stroke draft)
   * mid-pinch and fights the gesture. A reactive boolean can't do this job:
   * React re-renders only after that `touchstart` already ran.
   */
  isGesturing: () => boolean;
  /**
   * Reactive twin of {@link isGesturing} for props that Konva reads between
   * events (`draggable`, `listening`) — flip them off while gesturing.
   */
  gesturing: boolean;
}

const DEFAULT_MIN = 0.4;
const DEFAULT_MAX = 4;

export function usePinchZoom(options: UsePinchZoomOptions = {}): UsePinchZoomResult {
  const min = options.minScale ?? DEFAULT_MIN;
  const max = options.maxScale ?? DEFAULT_MAX;
  const initial = options.initial ?? { scale: 1, x: 0, y: 0 };

  const containerRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewportState] = useState<Viewport>(initial);
  const [gesturing, setGesturing] = useState(false);
  // Mirror into a ref so the (once-attached) native listeners always see the
  // current value without re-binding on every viewport change — re-binding
  // mid-gesture used to drop events.
  const viewportRef = useRef<Viewport>(viewport);
  const setViewport = useCallback((v: Viewport) => {
    viewportRef.current = v;
    setViewportState(v);
  }, []);

  const onGestureStartRef = useRef(options.onGestureStart);
  onGestureStartRef.current = options.onGestureStart;

  // Track active touch pointers for the native pinch gesture.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartRef = useRef<{
    distance: number;
    midpoint: { x: number; y: number };
    viewport: Viewport;
  } | null>(null);

  const reset = useCallback(() => setViewport(initial), [initial, setViewport]);

  // Wheel-based zoom centered around cursor (desktop convenience).
  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const direction = e.deltaY < 0 ? 1 : -1;
      const factor = 1 + direction * 0.12;
      const vp = viewportRef.current;
      const next = Math.min(max, Math.max(min, vp.scale * factor));
      if (next === vp.scale) return;
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      // Keep the point under the cursor stable in board coords.
      const ratio = next / vp.scale;
      setViewport({
        scale: next,
        x: cx - (cx - vp.x) * ratio,
        y: cy - (cy - vp.y) * ratio,
      });
    },
    [min, max, setViewport],
  );

  // Multi-touch pinch/pan via native PointerEvents. Attached ONCE per mount.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Without this, iOS Safari owns two-finger gestures (page pinch-zoom /
    // scroll) and cancels our pointer stream — the single most important
    // line for tablet support.
    const prevTouchAction = el.style.touchAction;
    el.style.touchAction = "none";

    /** (Re-)baseline the pinch from the two current pointer positions. */
    const baseline = () => {
      const [a, b] = Array.from(pointersRef.current.values());
      if (!a || !b) return;
      pinchStartRef.current = {
        distance: distance(a, b),
        midpoint: midpoint(a, b),
        viewport: viewportRef.current,
      };
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2) {
        // Cancel FIRST: aborting a Konva stage drag fires `dragend`, whose
        // handler syncs the dragged position into the viewport. Baselining
        // after that sees the settled value — the other way round the first
        // pinch frame would jump back to the pre-drag position.
        onGestureStartRef.current?.();
        baseline();
        setGesturing(true);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size !== 2) return;
      if (!pinchStartRef.current) baseline();
      const start = pinchStartRef.current;
      if (!start) return;
      const [a, b] = Array.from(pointersRef.current.values());
      if (!a || !b) return;
      const dist = distance(a, b);
      const rawScale = (dist / start.distance) * start.viewport.scale;
      const nextScale = Math.min(max, Math.max(min, rawScale));
      const ratio = nextScale / start.viewport.scale;
      const rect = el.getBoundingClientRect();
      const mid = midpoint(a, b);
      const cx = mid.x - rect.left;
      const cy = mid.y - rect.top;
      const startCx = start.midpoint.x - rect.left;
      const startCy = start.midpoint.y - rect.top;
      // Midpoint translation pans; spread change zooms around the midpoint —
      // together this is the familiar two-finger pan+zoom in one gesture.
      setViewport({
        scale: nextScale,
        x: cx - (startCx - start.viewport.x) * ratio,
        y: cy - (startCy - start.viewport.y) * ratio,
      });
      e.preventDefault();
    };

    const onPointerEnd = (e: PointerEvent) => {
      if (!pointersRef.current.delete(e.pointerId)) return;
      // Whatever the remaining count, the old baseline is stale. If exactly
      // two fingers remain (3→2), re-baseline so the gesture continues
      // seamlessly instead of jumping.
      pinchStartRef.current = null;
      if (pointersRef.current.size === 2) baseline();
      else if (pointersRef.current.size < 2) setGesturing(false);
    };

    // Safari-proprietary gesture events — swallow them so the PAGE never
    // zooms underneath our canvas in a regular browser tab.
    const swallow = (e: Event) => e.preventDefault();

    el.addEventListener("pointerdown", onPointerDown);
    // Move/up on window: fingers may drift off the container mid-gesture.
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
    el.addEventListener("gesturestart", swallow);
    el.addEventListener("gesturechange", swallow);
    el.addEventListener("gestureend", swallow);

    return () => {
      el.style.touchAction = prevTouchAction;
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
      el.removeEventListener("gesturestart", swallow);
      el.removeEventListener("gesturechange", swallow);
      el.removeEventListener("gestureend", swallow);
      pointersRef.current.clear();
      pinchStartRef.current = null;
    };
  }, [min, max, setViewport]);

  const isGesturing = useCallback(() => pointersRef.current.size >= 2, []);

  return { viewport, setViewport, reset, containerRef, onWheel, isGesturing, gesturing };
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

function midpoint(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Fit a board of `(boardW, boardH)` into a container of `(viewW, viewH)`. */
export function fitViewport(
  boardW: number,
  boardH: number,
  viewW: number,
  viewH: number,
  padding = 16,
): Viewport {
  const sx = (viewW - padding * 2) / boardW;
  const sy = (viewH - padding * 2) / boardH;
  const scale = Math.min(sx, sy);
  return {
    scale,
    x: (viewW - boardW * scale) / 2,
    y: (viewH - boardH * scale) / 2,
  };
}
